import React, { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, Building2, Coins, Mail, Save, Send, ChevronDown, ChevronUp, Check, Plus, X, Bell } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Toggle = ({ enabled, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!enabled)}
    className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? "bg-[#1CA3B0]" : "bg-slate-300"}`}
    data-testid={testId}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-6" : ""}`} />
  </button>
);

const PaymentMethodCard = ({ icon: Icon, title, description, method, config, onSave }) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(config || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal(config || {}); }, [config]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(method, local);
    setSaving(false);
  };

  const fields = {
    stripe: [
      { key: "publishable_key", label: "Publishable Key", type: "text" },
      { key: "secret_key", label: "Secret Key", type: "password" },
    ],
    paypal: [
      { key: "client_id", label: "Client ID", type: "text" },
      { key: "secret", label: "Secret", type: "password" },
      { key: "merchant_id", label: "Merchant ID", type: "text" },
    ],
    bank: [
      { key: "bank_name", label: "Emri i Bankes", type: "text" },
      { key: "iban", label: "IBAN", type: "text" },
      { key: "swift", label: "SWIFT / BIC", type: "text" },
      { key: "account_holder", label: "Mbajtesi i Llogarise", type: "text" },
      { key: "reference", label: "Reference / Shenime", type: "text" },
    ],
    pok: [
      { key: "merchant_id", label: "Merchant ID", type: "text" },
      { key: "key_id", label: "Key ID", type: "text" },
      { key: "key_secret", label: "Key Secret", type: "password" },
    ],
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid={`payment-${method}`}>
      <div
        className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(29,136,168,0.1)" }}>
            <Icon size={20} className="text-[#1CA3B0]" />
          </div>
          <div>
            <p className="font-semibold text-sm text-[#0A0D10]">{title}</p>
            <p className="text-xs text-[#828D9E]">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Toggle enabled={local.enabled || false} onChange={(v) => setLocal({ ...local, enabled: v })} testId={`toggle-${method}`} />
          {open ? <ChevronUp size={18} className="text-[#828D9E]" /> : <ChevronDown size={18} className="text-[#828D9E]" />}
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-5 space-y-4 bg-slate-50/50">
          {(fields[method] || []).map((f) => (
            <div key={f.key}>
              <Label className="text-xs font-semibold text-[#4A5568] mb-1.5 block">{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea
                  value={local[f.key] || ""}
                  onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                  rows={3}
                  className="rounded-lg border-slate-200 bg-white text-sm"
                  data-testid={`input-${method}-${f.key}`}
                />
              ) : (
                <Input
                  type={f.type}
                  value={local[f.key] || ""}
                  onChange={(e) => setLocal({ ...local, [f.key]: e.target.value })}
                  className="rounded-lg border-slate-200 bg-white text-sm"
                  data-testid={`input-${method}-${f.key}`}
                />
              )}
            </div>
          ))}
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 transition-all disabled:opacity-50"
            style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }}
            data-testid={`save-${method}`}
          >
            <Save size={14} /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      )}
    </div>
  );
};

const SmtpSettings = () => {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings/smtp`).then((r) => setConfig(r.data.value || {})).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.put(`${API}/settings/smtp`, { value: config });
      setMsg("Konfigurimi SMTP u ruajt!");
    } catch { setMsg("Gabim gjate ruajtjes."); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setMsg("");
    try {
      const res = await axios.post(`${API}/settings/smtp/test`, { to_email: testEmail });
      setMsg(res.data.message);
    } catch (err) { setMsg(err.response?.data?.detail || "Dergimi deshtoi."); }
    setTesting(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="smtp-settings">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(29,136,168,0.1)" }}>
          <Mail size={20} className="text-[#1CA3B0]" />
        </div>
        <div>
          <h3 className="font-bold text-base text-[#0A0D10]">Email SMTP</h3>
          <p className="text-xs text-[#828D9E]">Konfiguro dergimin e emaileve nga serveri juaj SMTP</p>
        </div>
        <div className="ml-auto">
          <Toggle enabled={config.enabled || false} onChange={(v) => setConfig({ ...config, enabled: v })} testId="toggle-smtp" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { key: "host", label: "SMTP Host", placeholder: "smtp.tuajdomain.com" },
          { key: "port", label: "Port", placeholder: "587" },
          { key: "username", label: "Username", placeholder: "user@domain.com" },
          { key: "password", label: "Password", placeholder: "********", type: "password" },
          { key: "from_email", label: "From Email", placeholder: "info@aligners.academy" },
          { key: "from_name", label: "From Name", placeholder: "Albanian Aligners Academy" },
        ].map((f) => (
          <div key={f.key}>
            <Label className="text-xs font-semibold text-[#4A5568] mb-1.5 block">{f.label}</Label>
            <Input
              type={f.type || "text"}
              value={config[f.key] || ""}
              onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="rounded-lg border-slate-200 bg-slate-50 text-sm"
              data-testid={`smtp-${f.key}`}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <label className="flex items-center gap-2 text-sm text-[#4A5568]">
          <input type="checkbox" checked={config.use_tls !== false} onChange={(e) => setConfig({ ...config, use_tls: e.target.checked })} className="rounded border-slate-300" data-testid="smtp-tls" />
          Perdor TLS
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-6">
        <button onClick={handleSave} disabled={saving} className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="save-smtp">
          <Save size={14} /> {saving ? "Duke ruajtur..." : "Ruaj Konfigurimin"}
        </button>
        <div className="flex items-center gap-2">
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email.com" className="rounded-lg border-slate-200 text-sm w-48" data-testid="smtp-test-email" />
          <button onClick={handleTest} disabled={testing} className="rounded-lg px-4 py-2 text-sm font-semibold border border-[#1CA3B0] text-[#1CA3B0] inline-flex items-center gap-2 hover:bg-[#1CA3B0]/5 disabled:opacity-50" data-testid="test-smtp-btn">
            <Send size={14} /> {testing ? "Duke testuar..." : "Test"}
          </button>
        </div>
      </div>
      {msg && <p className="text-sm mt-3 font-medium text-[#08707A]" data-testid="smtp-msg">{msg}</p>}
    </div>
  );
};

const NotificationEmailsSettings = () => {
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/settings/notifications`)
      .then((r) => setEmails(((r.data && r.data.value) || {}).extra_emails || []))
      .catch(() => {});
  }, []);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

  const addEmail = () => {
    const e = input.trim().toLowerCase();
    setMsg("");
    if (!isValidEmail(e)) {
      setMsg("Formati i email-it nuk eshte i vlefshem.");
      return;
    }
    if (emails.includes(e)) {
      setMsg("Ky email eshte tashme ne liste.");
      return;
    }
    setEmails([...emails, e]);
    setInput("");
  };

  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const handleSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      await axios.put(`${API}/settings/notifications`, { value: { extra_emails: emails } });
      setMsg("Lista u ruajt me sukses. Nga tani, njoftimet dergohen tek te gjitha keto adresa.");
    } catch (_) {
      setMsg("Ruajtja deshtoi. Provoni perseri.");
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-6" data-testid="notification-emails-settings">
      <div className="flex items-center gap-3 mb-5 pb-3 border-b border-slate-100">
        <Bell size={18} className="text-[#1CA3B0]" />
        <div>
          <h3 className="font-bold text-base text-[#0A0D10]">Email-e Njoftimi</h3>
          <p className="text-xs text-[#828D9E]">Cdo email i shtuar ketu do te marre njoftim automatik kur:
            <br />• Regjistrohet nje pjesemarres i ri • Behet nje pagese • Vjen nje mesazh kontakti • Vjen nje lead i ri
          </p>
        </div>
      </div>

      <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
        <p className="text-xs text-[#4A5568]">
          <strong>Primary:</strong> <span className="font-mono text-[#08707A]">info@aligners.academy</span> (gjithmone ne liste, nuk mund te fshihet).
        </p>
      </div>

      {emails.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="notif-email-list">
          {emails.map((e, idx) => (
            <div key={e} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200" data-testid={`notif-email-row-${idx}`}>
              <span className="text-sm font-mono text-[#0A0D10]">{e}</span>
              <button
                type="button"
                onClick={() => removeEmail(e)}
                className="text-red-500 hover:bg-red-50 rounded-md w-7 h-7 flex items-center justify-center transition-colors"
                aria-label={`Fshij ${e}`}
                data-testid={`notif-email-remove-${idx}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
          placeholder="email@example.com"
          className="rounded-lg border-slate-200 bg-slate-50 flex-1"
          data-testid="notif-email-input"
        />
        <button
          type="button"
          onClick={addEmail}
          className="rounded-lg px-4 py-2 text-sm font-semibold border border-[#08707A] text-[#08707A] hover:bg-[#08707A]/5 inline-flex items-center gap-1.5"
          data-testid="notif-email-add-btn"
        >
          <Plus size={14} /> Shto
        </button>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-50"
        style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }}
        data-testid="save-notif-emails"
      >
        <Save size={14} /> {saving ? "Duke ruajtur..." : "Ruaj Listen"}
      </button>

      {msg && <p className="text-sm mt-3 font-medium text-[#08707A]" data-testid="notif-msg">{msg}</p>}
    </div>
  );
};

const AdminSettings = () => {
  const [paymentConfig, setPaymentConfig] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings/payment_methods`).then((r) => setPaymentConfig(r.data.value || {})).catch(() => {});
  }, []);

  const handleSavePayment = async (method, data) => {
    const updated = { ...paymentConfig, [method]: data };
    setPaymentConfig(updated);
    await axios.put(`${API}/settings/payment_methods`, { value: updated });
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="text-2xl font-bold font-['Inter_Tight'] mb-8">Konfigurime</h1>

      <div className="mb-10">
        <h2 className="text-lg font-bold text-[#0A0D10] mb-1">Metoda Pagesash</h2>
        <p className="text-sm text-[#828D9E] mb-5">Konfiguro metodat e pagesave per regjistrimet ne evente.</p>
        <div className="space-y-3">
          <PaymentMethodCard icon={CreditCard} title="Karte Krediti / Debiti" description="Pagesa online me karte (permes POK Payments)" method="pok" config={paymentConfig.pok} onSave={handleSavePayment} />
          <PaymentMethodCard icon={Coins} title="PayPal" description="Pagesa permes PayPal" method="paypal" config={paymentConfig.paypal} onSave={handleSavePayment} />
          <PaymentMethodCard icon={Building2} title="Transferte Bankare" description="Pagesa me transferte bankare" method="bank" config={paymentConfig.bank} onSave={handleSavePayment} />
        </div>
      </div>

      <SmtpSettings />
      <NotificationEmailsSettings />
    </div>
  );
};

export default AdminSettings;
